// NOTES
/**
 * DOCS:
 * https://alpinejs.dev/essentials/installation
 * 
 * STATE:
 * To grab state $store values inside JS:
 * Alpine.store('your_store_item').valueYouWant
 * Example value - Alpine.store('pid_7870').hasOdd
 * Example method - Alpine.store('pid_7870').updateHasOdd(true)
 * 
.*/

import Alpine from 'alpinejs'
import qsa from '../../helpers/qsa'
import sortProductsByOdd from '../shared/sortProductsByOdd'
import handleProductListViewed from '../shared/handleProductListViewed'
import displayResults from '../shared/displayResults'
import { tooltipContent, aoColorClasses } from '../shared/aoSettings'
import {
  aoButtonViewed,
  aoButtonClicked,
  aoResultViewed,
  aoBannerClicked
} from '../tagular'

// If we need to call Alpine elsewhere (optional).
window.Alpine = Alpine

// Grab loaded product boxes.
const productBoxes = qsa('article.box')
const hasApprovalOdds = document.querySelector('[data-has-approval-odds]')
  ? true
  : false

const handleSortPorducts = () => {
  // Sort products by Approval Odd if sortProducts is true
  if (
    Alpine.store('approval_odds').sortProducts &&
    Alpine.store('approval_odds').hasResults
  ) {
    Alpine.store('approval_odds').products.sort(sortProductsByOdd)
    // Grab parent container
    const cardsWrapper = document.querySelector('.cards-wrapper__inner')
    // Re-append using vanilla JS and not Alpine loop to keep base product box functionallity (tagular events, apply now, etc)
    Alpine.store('approval_odds').products.forEach(product => {
      cardsWrapper.appendChild(product.element)
    })
    // Update beam positions
    handleUpdatingBeamPosition()
  }
}

const handleDisplayResults = () => {
  // Update products and display odds.
  displayResults()
  if (Alpine.store('approval_odds').sortProducts) {
    // Sort the products
    handleSortPorducts()
    // Scroll to top of cards.
    document.querySelector('#cards-wrapper').scrollIntoView()
  }

  // Fire product list viewed event
  handleProductListViewed(productBoxes)
  // Update approval odds store.
  Alpine.store('approval_odds').updateHasResults()
}

const handleUpdatingBeamPosition = () => {
  // Find updated position if products sorted/filtered
  const shownProducts = qsa('article.box:not([style="display: none;"])')
  shownProducts.forEach((product, index) => {
    // Set for product viewed event
    product.setAttribute('data-beam-position', index)
    // Set for product clicked events
    qsa('[data-apply-button]', product).forEach(el => {
      el.setAttribute('data-rank', index)
    })
  })
}

// ALPINE JS LOGIC - STATE VIA $STORE
// -----------------------------------

// Top level Alpine state useing $store.
Alpine.store('approval_odds', {
  appOddsOn: true,
  hasResults: false,
  sortProducts: false,
  pidMaps: {}, // To map special offer PID to original marketplace PID.
  // set starting product order to the rendered card order from server.
  products: productBoxes.map(product => {
    const pid = product.getAttribute('data-product-id')
    return {
      pid,
      key: `pid_${pid}`,
      element: product,
      odd: null,
      toolTipContent: '',
      aoChanceColor: ''
    }
  }),
  tagular: {
    aoButtonViewed,
    aoButtonClicked,
    aoResultViewed,
    aoBannerClicked
  },
  handleDisplayResults,
  updateHasResults() {
    this.hasResults = true
  },
  updatePidMaps(newPID, originalPID) {
    this.pidMaps[newPID] = originalPID
  }
})

// We want to watch for sortProducts to change from preamp.
Alpine.effect(() => {
  const sortProducts = Alpine.store('approval_odds').sortProducts
  if (sortProducts) {
    handleSortPorducts()
  }
})

// Create Alpine state instance for each product PID with default values.
productBoxes.forEach(box => {
  const pid = box.getAttribute('data-product-id')
  const collationId = box.getAttribute('data-collation-id')
  const cardName = box.getAttribute('data-beam-name') || ''
  const cardIssuer = box.getAttribute('data-issuer-name') || ''
  Alpine.store(`pid_${pid}`, {
    annualFee: null,
    balanceApr: null,
    balanceAprPeriodUnit: null,
    balanceAprPeriodValue: null,
    bullets: [],
    categories: {
      preapproved: false,
      prequal: false,
      specialOffer: false
    },
    collationId,
    hasOdd: false,
    introOffer: null,
    introOfferDescription: null,
    isAoOffer: false,
    issuer: cardIssuer,
    name: cardName,
    odd: null,
    pid: pid,
    purchaseApr: null,
    purchaseAprPeriodUnit: null,
    purchaseAprPeriodValue: null,
    rateRange: null,
    regularApr: null,
    rewardRates: [],
    getBannerText() {
      let text = ''

      if (this.categories.preapproved) {
        text = 'PREAPPROVED'
      } else if (this.categories.prequal) {
        text = 'PREQUALIFIED'
      }

      if (text && this.categories.specialOffer) {
        text += ' & SPECIAL'
      }

      return `${text} OFFER`
    },
    getBannerTooltipText() {
      let text = ''

      if (this.categories.preapproved) {
        text = this.categories.specialOffer
          ? "Approval Odds Special Offers are offers from our partners that you can't ﬁnd anywhere else on Bankrate.com. This badge indicates that you meet certain criteria determined by the lender for a pre-approved offer. This is not a guarantee of approval. When making an approval decision, the lender may consider your credit score, any serious delinquency or bankruptcy on your credit report, your previous history with the lender, ability to pay, fraud risk and other eligibility requirements."
          : 'This badge indicates that you meet certain criteria determined by the lender for a pre-approved offer. This is not a guarantee of approval. When making an approval decision, the lender may consider your credit score, any serious delinquency or bankruptcy on your credit report, your previous history with the lender, ability to pay, fraud risk and other eligibility requirements.'
      } else if (this.categories.prequal) {
        text = this.categories.specialOffer
          ? "Being pre-qualified means you meet the issuer's initial criteria. Approval Odds Special Offers are offers from our partners that you can't ﬁnd anywhere else on Bankrate.com"
          : "Being pre-qualified means you meet the issuer's initial criteria."
      }

      return text
    },
    handleAoButtonClicked(position, name) {
      Alpine.store('approval_odds').tagular.aoButtonClicked(
        position,
        name,
        this.name
      )
    },
    handleAoResultViewed(position) {
      // Find updated position if products sorted/filtered
      const shownProducts = qsa('article.box:not([style="display: none;"])')
      if (shownProducts && shownProducts.indexOf(box) >= 0) {
        position = shownProducts.indexOf(box)
      }
      const odd = this.odd || 'null'
      Alpine.store('approval_odds').tagular.aoResultViewed(
        position,
        odd,
        this.name,
        this.issuer
      )
    },
    updateAnnualFee(annualFee) {
      this.annualFee = annualFee
    },
    updateBalanceApr(balanceApr) {
      this.balanceApr = balanceApr
    },
    updateBalanceAprPeriodUnit(balanceAprPeriodUnit) {
      this.balanceAprPeriodUnit = balanceAprPeriodUnit
    },
    updateBalanceAprPeriodValue(balanceAprPeriodValue) {
      this.balanceAprPeriodValue = balanceAprPeriodValue
    },
    updateBullets(bullets) {
      this.bullets = bullets
    },
    updateCategory(cat, value) {
      this.categories[cat] = value
    },
    updateHasOdd(hasOdd) {
      this.hasOdd = hasOdd
    },
    updateIntroOffer(introOffer) {
      this.introOffer = introOffer
    },
    updateIntroOfferDescription(introOfferDescription) {
      this.introOfferDescription = introOfferDescription
    },
    updateIsAoOffer() {
      this.isAoOffer = true
    },
    updateOdd(odd) {
      this.odd = odd
      // Add data attributes with odd value for product viewed/clicked events.
      box.setAttribute('data-beam-location', odd)
      qsa('[data-apply-button]', box).forEach(el => {
        el.setAttribute('data-location', odd)
        // For Card Art.
        const cardArt = el.querySelector('img')
        if (cardArt) {
          cardArt.setAttribute('data-location', odd)
        }

        // For Card Name Headers.
        const cardName = el.querySelector('.js-product-box__title')
        if (cardName) {
          cardName.setAttribute('data-location', odd)
        }
      })
    },
    updatePurchaseApr(purchaseApr) {
      this.purchaseApr = purchaseApr
    },
    updatePurchaseAprPeriodUnit(purchaseAprPeriodUnit) {
      this.purchaseAprPeriodUnit = purchaseAprPeriodUnit
    },
    updatePurchaseAprPeriodValue(purchaseAprPeriodValue) {
      this.purchaseAprPeriodValue = purchaseAprPeriodValue
    },
    updateRateRange(rateRange) {
      this.rateRange = rateRange
    },
    updateRegularApr(regularApr) {
      this.regularApr = regularApr
    },
    updateRewardRates(rewardRates) {
      this.rewardRates = rewardRates
    },
    updateTooltipContent(odd) {
      this.toolTipContent = tooltipContent[odd]
    },
    updateAoColor(odd) {
      this.aoChanceColor = aoColorClasses[odd]
    }
  })
})

// Display results if they already exist in session storage.
// Grab results from session storage.
if (sessionStorage.getItem('approvalOdds') && hasApprovalOdds) {
  Alpine.store('approval_odds').handleDisplayResults()
}
